import React, { useContext, useState } from "react";
import RatingCircles from "/src/helpers/components/ratingCircles.jsx";
import "./packageSearchResultItem.scss";
import { useMoneyFormatter } from "/src/helpers/Hooks/useMoneyFormatter";
import { DebugModeContext } from "/src/helpers/contexts/agentContext";
import { Tooltip, TooltipTrigger, TooltipContent } from "/src/helpers/components/tooltip.jsx";
import { InfoIcon } from "/src/helpers/components/icons.jsx";
import { Link, useLocation } from "react-router";
import PackageSearchResultFlightDetails from "./searchResultItem.flightDetails.jsx";
import PackageSearchResultCalendar from "./searchResultItem.calendar.jsx";
import OfficalRating from "/src/helpers/components/officalRating.jsx";
import ResultImageSlider from "./components/ResultImageSlider.jsx";
import ResultFeatures from "./components/ResultFeatures.jsx";
import { uspCardStyle, uspButtonStyle } from "../components/uspBadge.jsx";

export default function PackageSearchResultItem({ result }) {
    const [showFlightModal, setShowFlightModal] = useState(false);
    const [showCalendarModal, setShowCalendarModal] = useState(false);
    const [format, isPerPerson] = useMoneyFormatter();
    const [debugMode, umbracoAdminUrl] = useContext(DebugModeContext);
    const location = useLocation();

    const usp = result.hotel.uniqueSellingPoint;
    const viewDealButtonClass = usp ? "usp-button" : "bg-primary hover:bg-secondary";

    const linkState = { returnUrl: location.pathname + location.search, returnHotel: result.hotel.id };

    return (
        <div className="search-result-card" style={uspCardStyle(usp)}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-4 lg:gap-6">
                <ResultImageSlider images={result.hotel.images} usp={usp} />

                {/* Content Section */}
                <div className="flex flex-col justify-between">
                    <div>
                        <h3 className="flex lg:flex-row flex-col lg:items-center items-start text-2xl font-semibold text-primary mb-4">
                            {result.onlineBookable ? (
                                <Link to={result.url} state={linkState}>
                                    {result.hotel.name}
                                </Link>
                            ) : (
                                <span>{result.hotel.name}</span>
                            )}
                        </h3>
                        <nav className="flex mb-4 text-sm text-content_3">
                            {result.countryName && <span>{result.countryName}</span>}
                            {result.countryName && result.regionName && <span className="mx-2">&gt;</span>}
                            {result.regionName && <span>{result.regionName}</span>}
                            {result.regionName && result.resortName && <span className="mx-2">&gt;</span>}
                            {result.countryName && !result.regionName && result.resortName && (
                                <span className="mx-2">&gt;</span>
                            )}
                            {result.resortName && <span>{result.resortName}</span>}
                        </nav>
                        {/* Ratings */}
                        <div className="flex items-center gap-4 mb-4">
                            <OfficalRating rating={result.hotel.officialRating} />

                            {result.tripadvisorRating && (
                                <div className="flex items-center gap-1">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M8.12808 13.2447C8.12808 14.4473 7.17516 15.4224 6 15.4224C4.82484 15.4224 3.87192 14.4473 3.87192 13.2447C3.87192 12.0422 4.82484 11.0669 6 11.0669C7.17516 11.0669 8.12808 12.042 8.12808 13.2447ZM17.9947 11.0669C16.8195 11.0669 15.8666 12.042 15.8666 13.2447C15.8666 14.4473 16.8194 15.4224 17.9947 15.4224C19.1701 15.4224 20.1228 14.4473 20.1228 13.2447C20.1224 12.0427 19.1704 11.0682 17.9958 11.0676L17.9947 11.0669ZM23.9947 13.2447C23.9947 16.6337 21.3074 19.3814 17.9947 19.3814C16.4836 19.3841 15.0277 18.8004 13.9204 17.7482L12 19.8857L10.0788 17.7457C8.97167 18.7993 7.51519 19.3839 6.00329 19.3816C2.69292 19.3814 0.00657434 16.6335 0.00657434 13.2445C0.00540253 12.3907 0.17881 11.5462 0.515651 10.7651C0.852491 9.98405 1.34531 9.28374 1.96244 8.70919L0 6.52415H4.36025C8.97379 3.31096 15.0375 3.31096 19.6511 6.52415H24L22.0377 8.709C22.6551 9.28347 23.1481 9.98377 23.4852 10.7649C23.8222 11.546 23.9958 12.3906 23.9947 13.2445M10.0598 13.2445C10.0598 10.9509 8.24276 9.09134 6.00146 9.09134C3.76016 9.09134 1.94308 10.951 1.94308 13.2447C1.94308 15.5383 3.76016 17.3978 6.00146 17.3978C8.24276 17.3978 10.0597 15.5388 10.0598 13.2452V13.2445ZM16.5034 7.0442C15.0763 6.43579 13.5455 6.12256 11.9995 6.12256C10.4534 6.12256 8.92264 6.43579 7.49548 7.0442C10.0576 8.04768 12.0002 10.3919 12.0002 13.1249C12.0002 10.3921 13.9424 8.04768 16.5036 7.04458L16.5034 7.0442ZM22.0542 13.2445C22.0542 10.9509 20.2371 9.09134 17.9958 9.09134C15.7545 9.09134 13.9374 10.9507 13.9374 13.2443C13.9374 15.5379 15.7543 17.3974 17.9954 17.3974C20.2366 17.3974 22.0538 15.5381 22.0538 13.2443"
                                            fill="#002B11"
                                        />
                                    </svg>
                                    <span className="text-md font-medium text-gray-900">{result.tripadvisorRating}</span>
                                    <RatingCircles rating={result.tripadvisorRating} bgColour="bg-green-500" />
                                </div>
                            )}
                        </div>

                        {/* Price */}
                        <div className="flex flex-col xl:flex-row items-center mb-4">
                            <div className="inline-flex items-center gap-1 px-4 py-1 rounded-full bg-tertiary text-center w-1/1 xl:w-auto   ">
                                <span className="text-xs text-secondary">from</span>
                                <span className="text-2xl font-bold ">
                                    {format(result.price, { removePostfix: true, allowShortForm: true })}
                                </span>
                                {isPerPerson && <span className="text-xs text-secondary">pp</span>}
                                {!isPerPerson && <span className="text-xs text-secondary">Total</span>}
                            </div>
                            {result.localCharges && (
                                <Tooltip>
                                    <TooltipTrigger>
                                        <div className="">
                                            <InfoIcon size={30} />
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-emp_dark text-white p-3 rounded-lg w-max max-w-sm font-thin leading-none">
                                        <p className="pb-2">
                                            An additional cost of approx.{" "}
                                            {result.localCharges.amountInPoundsFormatted} (
                                            {result.localCharges.localCurrencyFormatted}) tax will be paid locally.
                                        </p>
                                        <p className="pb-2">
                                            Based on an exchange rate of ({result.localCharges.symbol}1{" "}
                                            {result.localCharges.currencyCode} = {result.localCharges.exchangeRate}){" "}
                                        </p>
                                        <p className="pb-2 text-sm">
                                            Exchange rate and local taxes may change before you pay at the hotel
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            )}
                        </div>

                        <ResultFeatures
                            result={result}
                            format={format}
                            isPerPerson={isPerPerson}
                            onShowCalendar={() => setShowCalendarModal(true)}
                            onShowFlight={() => setShowFlightModal(true)}
                        />
                    </div>

                    {/* See Availability Button (or call-centre-only message when not bookable online) */}
                    <div className="flex justify-end">
                        {result.onlineBookable ? (
                            <Link
                                to={result.url}
                                state={linkState}
                                style={uspButtonStyle(usp)}
                                className={`${viewDealButtonClass} text-white text-sm px-4 py-2 rounded-full font-medium transition-colors duration-200 flex items-center gap-2 w-auto`}
                            >
                                View deal
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M9 18L15 12L9 6"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </Link>
                        ) : (
                            <div
                                style={uspButtonStyle(usp)}
                                className={`${viewDealButtonClass} text-white text-sm px-4 py-2 rounded-full font-medium flex items-center gap-2 w-auto`}
                            >
                                <span>{result.onlineBookingButtonText}</span>
                                {result.onlineBookingMessage && (
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <div className="flex items-center">
                                                <InfoIcon size={20} />
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent className="bg-emp_dark text-white p-3 rounded-lg w-max max-w-sm font-thin leading-none">
                                            {result.onlineBookingMessage}
                                        </TooltipContent>
                                    </Tooltip>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                {showCalendarModal && (
                    <PackageSearchResultCalendar result={result} onClose={() => setShowCalendarModal(false)} />
                )}
                {showFlightModal && (
                    <PackageSearchResultFlightDetails result={result} onClose={() => setShowFlightModal(false)} />
                )}
            </div>
            {debugMode && (
                <div className="px-4 mt-4 -mb-6 -mx-6 bg-olympic_main flex gap-6 justify-between ">
                    <div className="bg-warning -mx-6 px-6">Debug</div>
                    <div>CPR: {result.hotel.id}</div>
                    <div>Source: {result.source}</div>
                    <div>Ref: {result.packageReference}</div>
                    <div>Local Charges: {result.localCharges ? result.localCharges.ids.join(", ") : "N/A"}</div>
                    <div>
                        <a
                            className="text-emp hover:text-emp_dark hover:underline"
                            target="_blank"
                            href={umbracoAdminUrl + "/umbraco#/content/content/edit/" + result.cmsId}
                        >
                            Open in Umbraco
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
}
